import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Typography, Button, Space, Radio, Tag, Empty, message, Tooltip, Spin,
  Statistic, Select, Input, Timeline as AntTimeline,
} from 'antd';
import {
  LeftOutlined, RightOutlined, PlusOutlined, ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchCalendarEvents, fetchCalendarDashboard, rescheduleEvent } from '../services/calendarService';
import { fetchSettings } from '../services/settingsService';
import { eventColor, colorMap, BASE_TYPE_COLORS, BRAND } from './calendarConstants';
import EventDrawer from './EventDrawer';
import EventFormModal from './EventFormModal';

const { Title, Text } = Typography;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CARDS = [
  { key: 'todayEvents', label: "Today's Events", color: BRAND },
  { key: 'upcoming', label: 'Upcoming', color: '#722ed1' },
  { key: 'overdue', label: 'Overdue', color: '#f5222d' },
  { key: 'completedToday', label: 'Completed Today', color: '#52c41a' },
  { key: 'siteVisitsToday', label: 'Site Visits Today', color: '#52c41a' },
  { key: 'officeVisits', label: 'Office Visits', color: '#fa8c16' },
  { key: 'meetings', label: 'Meetings', color: '#722ed1' },
  { key: 'calls', label: 'Calls', color: '#1890ff' },
  { key: 'materialSelection', label: 'Material Selection', color: '#eb2f96' },
  { key: 'quotationFollowups', label: 'Quotation Follow-ups', color: '#faad14' },
];

const Calendar = () => {
  const [viewType, setViewType] = useState('month');
  const [cursor, setCursor] = useState(dayjs());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({});
  const [typeColors, setTypeColors] = useState(BASE_TYPE_COLORS);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchSettings();
        setSettings(s);
        setTypeColors({ ...BASE_TYPE_COLORS, ...colorMap(s.followup_type), ...colorMap(s.event_type) });
      } catch { /* ignore */ }
    })();
  }, []);

  const range = useMemo(() => {
    if (viewType === 'day') return [cursor.startOf('day'), cursor.endOf('day')];
    if (viewType === 'week') return [cursor.startOf('week'), cursor.endOf('week')];
    if (viewType === 'agenda' || viewType === 'timeline') return [cursor.startOf('day'), cursor.add(60, 'day').endOf('day')];
    return [cursor.startOf('month').startOf('week'), cursor.endOf('month').endOf('week')];
  }, [viewType, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCalendarEvents({
        start: range[0].format('YYYY-MM-DD'), end: range[1].format('YYYY-MM-DD'), search, ...filters,
      });
      setEvents(data);
    } catch { message.error('Failed to load calendar'); }
    finally { setLoading(false); }
  }, [range, search, filters]);

  const loadStats = useCallback(async () => {
    try { setStats(await fetchCalendarDashboard()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (!e.EventAt) continue;
      const k = dayjs(e.EventAt).format('YYYY-MM-DD');
      (map[k] = map[k] || []).push(e);
    }
    Object.values(map).forEach(l => l.sort((a, b) => dayjs(a.EventAt) - dayjs(b.EventAt)));
    return map;
  }, [events]);

  const move = (dir) => {
    const unit = (viewType === 'agenda' || viewType === 'timeline') ? 'month' : viewType;
    setCursor(c => dir === 0 ? dayjs() : c.add(dir, unit));
  };

  const onDropDay = async (dayKey) => {
    if (!dragId) return;
    const ev = events.find(e => e.id === dragId);
    setDragId(null);
    if (!ev) return;
    const oldT = dayjs(ev.EventAt);
    const newAt = dayjs(dayKey).hour(oldT.hour()).minute(oldT.minute());
    if (newAt.format('YYYY-MM-DD') === oldT.format('YYYY-MM-DD')) return;
    try {
      await rescheduleEvent(ev.source, ev.id, newAt.format('YYYY-MM-DD HH:mm:ss'));
      message.success(`Moved to ${newAt.format('DD MMM')}`);
      load(); loadStats();
    } catch { message.error('Failed to reschedule'); }
  };

  const setFilter = (k, v) => setFilters(p => { const n = { ...p }; if (!v) delete n[k]; else n[k] = v; return n; });

  const onSaved = () => { setFormOpen(false); setEditing(null); load(); loadStats(); };
  const onChanged = () => { load(); loadStats(); };
  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const editCustom = (raw) => { if (raw) { setDetail(null); setEditing(raw); setFormOpen(true); } };

  const Chip = ({ ev, showTime = true }) => (
    <div draggable onDragStart={() => setDragId(ev.id)}
      onClick={(e) => { e.stopPropagation(); setDetail(ev); }}
      title={`${ev.CustomerName || ev.Title} · ${ev.EventType || ''}`}
      style={{
        background: eventColor(ev, typeColors), color: '#fff', borderRadius: 4, padding: '1px 6px',
        fontSize: 11, marginBottom: 2, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis', opacity: ev.Status === 'Completed' ? 0.65 : 1,
      }}>
      {showTime && dayjs(ev.EventAt).format('HH:mm')} {ev.CustomerName || ev.Title}
    </div>
  );

  const monthView = () => {
    const days = []; let d = range[0];
    while (d.isBefore(range[1])) { days.push(d); d = d.add(1, 'day'); }
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {WEEKDAYS.map(w => <div key={w} style={{ textAlign: 'center', padding: '6px 0', fontWeight: 600, color: '#8c8c8c' }}>{w}</div>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
          {days.map(day => {
            const key = day.format('YYYY-MM-DD');
            const inMonth = day.month() === cursor.month();
            const isToday = day.isSame(dayjs(), 'day');
            const list = eventsByDay[key] || [];
            return (
              <div key={key} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropDay(key)}
                onClick={() => { setViewType('day'); setCursor(day); }}
                style={{ minHeight: 104, borderRight: '1px solid #f5f5f5', borderBottom: '1px solid #f5f5f5', padding: 4, background: inMonth ? '#fff' : '#fafafa', cursor: 'pointer' }}>
                <div style={{ textAlign: 'right', padding: '0 4px' }}>
                  <span style={{ display: 'inline-block', minWidth: 22, height: 22, lineHeight: '22px', textAlign: 'center', borderRadius: '50%', fontSize: 12, background: isToday ? BRAND : 'transparent', color: isToday ? '#fff' : (inMonth ? '#000' : '#bfbfbf') }}>{day.date()}</span>
                </div>
                {list.slice(0, 3).map(ev => <Chip key={ev.id} ev={ev} />)}
                {list.length > 3 && <Text type="secondary" style={{ fontSize: 11 }}>+{list.length - 3} more</Text>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const weekView = () => {
    const days = []; let d = range[0];
    while (d.isBefore(range[1])) { days.push(d); d = d.add(1, 'day'); }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
        {days.map(day => {
          const key = day.format('YYYY-MM-DD');
          const list = eventsByDay[key] || [];
          const isToday = day.isSame(dayjs(), 'day');
          return (
            <div key={key} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropDay(key)}
              style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, minHeight: 220, background: isToday ? '#fff7f0' : '#fff' }}>
              <div style={{ textAlign: 'center', marginBottom: 8, fontWeight: 600 }}>{day.format('ddd')}<br /><span style={{ color: isToday ? BRAND : '#000' }}>{day.format('DD')}</span></div>
              {list.length ? list.map(ev => <Chip key={ev.id} ev={ev} />) : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>}
            </div>
          );
        })}
      </div>
    );
  };

  const dayView = () => {
    const key = cursor.format('YYYY-MM-DD');
    const list = eventsByDay[key] || [];
    return list.length === 0 ? <Empty description={`No events on ${cursor.format('DD MMM YYYY')}`} /> : list.map(ev => (
      <div key={ev.id} onClick={() => setDetail(ev)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8, cursor: 'pointer' }}>
        <div style={{ width: 6, height: 40, borderRadius: 3, background: eventColor(ev, typeColors) }} />
        <div style={{ width: 80 }}><Text strong>{dayjs(ev.EventAt).format('hh:mm A')}</Text></div>
        <div style={{ flex: 1 }}>
          <Text strong>{ev.CustomerName || ev.Title}</Text>{' '}
          {ev.EventType && <Tag color={typeColors[ev.EventType] || 'blue'}>{ev.EventType}</Tag>}
          <div><Text type="secondary" style={{ fontSize: 12 }}>{ev.CustomerMobile || ''} {ev.City ? `· ${ev.City}` : ''} {ev.ExecutiveName ? `· ${ev.ExecutiveName}` : ''}</Text></div>
        </div>
      </div>
    ));
  };

  const agendaView = () => {
    const keys = Object.keys(eventsByDay).sort();
    if (!keys.length) return <Empty description="No upcoming events" />;
    return keys.map(key => (
      <div key={key} style={{ marginBottom: 16 }}>
        <Text strong style={{ color: BRAND }}>{dayjs(key).format('dddd, DD MMM YYYY')}</Text>
        <div style={{ marginTop: 8 }}>
          {eventsByDay[key].map(ev => (
            <div key={ev.id} onClick={() => setDetail(ev)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 6, cursor: 'pointer' }}>
              <div style={{ width: 6, height: 30, borderRadius: 3, background: eventColor(ev, typeColors) }} />
              <Text style={{ width: 80 }}>{dayjs(ev.EventAt).format('hh:mm A')}</Text>
              <Text strong>{ev.CustomerName || ev.Title}</Text>
              {ev.EventType && <Tag color={typeColors[ev.EventType] || 'blue'}>{ev.EventType}</Tag>}
            </div>
          ))}
        </div>
      </div>
    ));
  };

  const timelineView = () => {
    const sorted = [...events].sort((a, b) => dayjs(a.EventAt) - dayjs(b.EventAt));
    if (!sorted.length) return <Empty description="No events" />;
    return (
      <AntTimeline
        mode="left"
        items={sorted.map(ev => ({
          color: eventColor(ev, typeColors),
          label: dayjs(ev.EventAt).format('DD MMM, hh:mm A'),
          children: (
            <div style={{ cursor: 'pointer' }} onClick={() => setDetail(ev)}>
              <Text strong>{ev.CustomerName || ev.Title}</Text>{' '}
              {ev.EventType && <Tag color={typeColors[ev.EventType] || 'blue'}>{ev.EventType}</Tag>}
              <div><Text type="secondary" style={{ fontSize: 12 }}>{ev.ExecutiveName || ''} {ev.City ? `· ${ev.City}` : ''}</Text></div>
            </div>
          ),
        }))}
      />
    );
  };

  // Clicking a summary card filters the calendar accordingly.
  const cardAction = (key) => {
    const map = {
      overdue: { Status: 'Overdue' }, upcoming: { Status: 'Pending' }, completedToday: { Status: 'Completed' },
      siteVisitsToday: { EventType: 'Site Visit' }, officeVisits: { EventType: 'Office Visit' },
      materialSelection: { EventType: 'Material Selection' }, quotationFollowups: { EventType: 'Quotation Follow-up' },
      calls: { EventType: 'Call' },
    };
    if (key === 'todayEvents') { setFilters({}); setViewType('day'); setCursor(dayjs()); return; }
    setFilters(map[key] || {});
    setViewType('agenda');
  };

  const label = viewType === 'month' ? cursor.format('MMMM YYYY')
    : viewType === 'week' ? `${range[0].format('DD MMM')} – ${range[1].format('DD MMM YYYY')}`
    : viewType === 'day' ? cursor.format('dddd, DD MMM YYYY') : 'Next 60 days';

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }} gutter={[8, 8]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Calendar</Title>
          <Text type="secondary">Unified schedule across the whole CRM</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ borderRadius: 6 }}>New Event</Button>
        </Col>
      </Row>

      {/* Summary cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {CARDS.map(c => (
          <Col xs={12} sm={8} md={6} lg={4} key={c.key}>
            <Card bordered={false} hoverable style={{ borderRadius: 8, cursor: 'pointer' }} bodyStyle={{ padding: '12px 16px' }}
              onClick={() => cardAction(c.key)}>
              <Statistic title={c.label} value={Number(stats[c.key]) || 0} valueStyle={{ color: c.color, fontSize: 20 }} />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Toolbar */}
      <Card bordered={false} style={{ borderRadius: 8, marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
        <Row gutter={[8, 8]} align="middle" justify="space-between">
          <Col>
            <Space wrap>
              <Button onClick={() => move(0)}>Today</Button>
              <Button icon={<LeftOutlined />} onClick={() => move(-1)} />
              <Button icon={<RightOutlined />} onClick={() => move(1)} />
              <Text strong style={{ minWidth: 150, display: 'inline-block', textAlign: 'center' }}>{label}</Text>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              <Input.Search placeholder="Search name / mobile" allowClear style={{ width: 180 }}
                enterButton={<SearchOutlined />} onSearch={setSearch}
                onChange={(e) => { if (!e.target.value) setSearch(''); }} />
              <Select allowClear placeholder="Type" style={{ width: 150 }} onChange={(v) => setFilter('EventType', v)} showSearch
                options={[...(settings.followup_type || []), ...(settings.event_type || [])].map(t => ({ value: t.Value, label: t.Label }))} />
              <Select allowClear placeholder="Executive" style={{ width: 130 }} onChange={(v) => setFilter('ExecutiveName', v)}
                options={(settings.executive || []).map(e => ({ value: e.Value, label: e.Label }))} />
              <Select allowClear placeholder="Priority" style={{ width: 110 }} onChange={(v) => setFilter('Priority', v)}
                options={(settings.priority_color || []).map(p => ({ value: p.Value, label: p.Label }))} />
              <Select allowClear placeholder="Status" style={{ width: 120 }} onChange={(v) => setFilter('Status', v)}
                options={['Pending', 'Completed', 'Overdue', 'Cancelled'].map(s => ({ value: s, label: s }))} />
              <Radio.Group value={viewType} onChange={(e) => setViewType(e.target.value)} optionType="button" buttonStyle="solid"
                options={[{ label: 'Month', value: 'month' }, { label: 'Week', value: 'week' }, { label: 'Day', value: 'day' }, { label: 'Agenda', value: 'agenda' }, { label: 'Timeline', value: 'timeline' }]} />
              <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={() => { load(); loadStats(); }} /></Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Legend */}
      <Space wrap style={{ marginBottom: 12, padding: '0 8px' }}>
        {Object.entries(typeColors).map(([k, c]) => (
          <span key={k} style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c, marginRight: 4 }} />{k}</span>
        ))}
        <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f5222d', marginRight: 4 }} />Overdue</span>
        <span style={{ fontSize: 12 }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#bfbfbf', marginRight: 4 }} />Completed</span>
      </Space>

      <Card bordered={false} style={{ borderRadius: 8 }} bodyStyle={{ padding: 16 }}>
        {loading ? <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div> : (
          viewType === 'month' ? monthView()
            : viewType === 'week' ? weekView()
            : viewType === 'day' ? dayView()
            : viewType === 'agenda' ? agendaView()
            : timelineView()
        )}
      </Card>

      <EventDrawer open={!!detail} event={detail} onClose={() => setDetail(null)} onChanged={onChanged} onEditCustom={editCustom} />
      <EventFormModal open={formOpen} editing={editing} settings={settings} onClose={() => { setFormOpen(false); setEditing(null); }} onSaved={onSaved} />
    </div>
  );
};

export default Calendar;
